/* eslint-disable react/jsx-handler-names */
import React from 'react';
import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz-trans/client';

import { useHeaderLinks, useBaseLinks } from '../../../context/ExternalLink';
import { useThemeFlag } from '../../../../base/ThemeProvider';

import Button from '../../Button';
import LanguageSwitcher from '../../LanguageSwitcher';

import HeaderLink from '../HeaderLink';
import HeaderItem from '../HeaderItem';
import HeaderLogin from '../HeaderLogin';
import HeaderItems from '../HeaderItems';

import linkShape from '../shapes/link.shape';
import loginShape from '../shapes/login.shape';
import themeShape, { DARK_THEME, LIGHT_THEME } from '../shapes/theme.shape';

const HeaderNav = ({
  locales,
  login,
  color,
  isTopTenantVariant,
  isTransparent,
  showLogin,
  ...links
}) => {
  const headerLinks = useHeaderLinks();
  const localeLinks = useBaseLinks();

  const { listYourProperty, tenantLandingPage, howItWorks, favorites, help } =
    links;

  const newButtonsEnabled = useThemeFlag('newButtons');

  return (
    <HeaderItems>
      {!!listYourProperty && (
        <HeaderItem>
          <Button
            alt
            color={
              newButtonsEnabled && isTransparent && color === LIGHT_THEME
                ? 'tertiary-light'
                : 'interaction'
            }
            className="ga-home-publish-your-property-top header-button"
            href={headerLinks.publishYourProperty}
            size="small"
            {...links.listYourProperty}
          >
            {links.listYourProperty.label ||
              trans('landingpage.button.landlord')}
          </Button>
        </HeaderItem>
      )}
      {!!tenantLandingPage && (
        <HeaderItem>
          <Button
            alt={color === 'dark'}
            color={color === 'light' ? 'filled' : 'primary'}
            className="ga-home-tenant-landing-page-top header-button"
            href={headerLinks.landing}
            size="small"
            {...links.tenantLandingPage}
          >
            {links.tenantLandingPage.label ||
              trans('landingpage.button.tenant')}
          </Button>
        </HeaderItem>
      )}

      {!!howItWorks && (
        <HeaderItem>
          <HeaderLink
            target="_blank"
            className="ga-menu-faqs"
            color={color}
            href={headerLinks.howItWorks}
            {...links.howItWorks}
          >
            {links.howItWorks.label || trans('how_it_works.title')}
          </HeaderLink>
        </HeaderItem>
      )}

      {!!favorites && (
        <HeaderItem>
          <HeaderLink
            className="ga-menu-favorites"
            target="_blank"
            color={color}
            href={headerLinks.favorites}
            rel="nofollow"
            {...links.favorites}
          >
            {links.favorites.label || trans('favorites.title.favorites')}
          </HeaderLink>
        </HeaderItem>
      )}

      {showLogin || !!login ? (
        <HeaderLogin
          {...login}
          color={color}
          isMobile={false}
          isTopTenantVariant={isTopTenantVariant}
        />
      ) : null}

      {!!help && (
        <HeaderItem>
          <HeaderLink
            href={headerLinks.support}
            color={color}
            data-test="header-nav-help"
            target="_self"
            {...links.help}
          >
            {links.help.label || trans('help.title')}
          </HeaderLink>
        </HeaderItem>
      )}

      {!!locales.onChange && (
        <HeaderItem>
          <HeaderLink color={color}>
            <LanguageSwitcher
              theme={color}
              data-test="language-switcher"
              locales={locales.list || Object.keys(localeLinks)}
              current={locales.current}
              onChange={locales.onChange}
              abbreviated
            />
          </HeaderLink>
        </HeaderItem>
      )}
    </HeaderItems>
  );
};

HeaderNav.propTypes = {
  listYourProperty: PropTypes.shape(linkShape),
  howItWorks: PropTypes.shape(linkShape),
  favorites: PropTypes.shape(linkShape),
  help: PropTypes.shape(linkShape),
  tenantLandingPage: PropTypes.shape(linkShape),
  locales: PropTypes.shape({
    current: PropTypes.string.isRequired,
    list: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func
  }),
  login: PropTypes.shape(loginShape),
  color: themeShape,
  isTopTenantVariant: PropTypes.bool,
  isTransparent: PropTypes.bool,
  showLogin: PropTypes.bool.isRequired
};

HeaderNav.defaultProps = {
  listYourProperty: {},
  howItWorks: {},
  favorites: {},
  help: {},
  locales: PropTypes.shape({
    current: 'es'
  }),
  login: {},
  color: DARK_THEME,
  tenantLandingPage: null,
  isTopTenantVariant: false,
  isTransparent: false
};

export default HeaderNav;
